import { collapseOpenItems } from './collapse-items.js';
import { bindMobileExpandDelegates, createToggleRowHelpers } from './mobile-expand.js';
import { nextSortDir } from './render-ui.js';

export function bindSort(rootEl, getState, applySort) {
    rootEl.addEventListener('click', (e) => {
        const sortable = e.target.closest('.sortable');
        if (!sortable || !rootEl.contains(sortable)) {
            return;
        }
        const key = sortable.dataset.sort;
        if (!key) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const state = getState();
        applySort(key, nextSortDir(state.sortKey, state.sortDir, key));
    });
}

export function bindGridEvents(ctx) {
    const {
        grid,
        body,
        options,
        listen,
        lifetime,
        state,
        api,
        statusFilterIds,
        recurringStatusFilterIds,
        typeFilterIds,
        statusStorageKey,
        recurringStatusStorageKey,
        typeStorageKey,
        searchStorageKey,
        dateRangeEnabled,
        dateFromSelector,
        dateToSelector,
        searchSelector,
        lengthEl,
        defaultPageLength,
        selectAllId,
        checkboxClass,
        filterRenderDeferMs,
    } = ctx;

    function bindDateRange() {
        if (!dateRangeEnabled) return;
        document.addEventListener('change', (e) => {
            if (!e.target.matches(dateFromSelector) && !e.target.matches(dateToSelector)) return;
            if (e.target.matches(dateFromSelector)) state.dateFrom = e.target.value;
            else state.dateTo = e.target.value;
            api.syncDateRangeInputs();
            state.page = 1;
            api.render();
            api.notifyDateRangeChange();
        }, listen);
    }

    function bindSearch() {
        if (!searchSelector) return;
        let debounceId = 0;
        const applySearch = (value) => {
            if (!grid.isConnected) return;
            const v = String(value ?? '');
            document.querySelectorAll(searchSelector).forEach(el => { el.value = v; });
            try { sessionStorage.setItem(searchStorageKey, v); } catch (error) { console.error(error); }
            state.searchQuery = v;
            state.page = 1;
            api.render();
        };
        const onInput = (e) => {
            if (!e.target.matches?.(searchSelector)) return;
            clearTimeout(debounceId);
            debounceId = setTimeout(() => applySearch(e.target.value), 150);
        };
        const onBlur = (e) => {
            if (!e.target.matches?.(searchSelector)) return;
            clearTimeout(debounceId);
            applySearch(e.target.value);
        };
        document.addEventListener('input', onInput, listen);
        document.addEventListener('blur', onBlur, { capture: true, signal: lifetime.signal });
    }

    function bindFilter(ids, storageKey, applyValue) {
        if (!ids.length) return;
        document.addEventListener('filter-changed', (e) => {
            const d = e.detail || {};
            if (!ids.includes(d.id)) return;
            const value = d.value || '';
            applyValue(value);
            try { sessionStorage.setItem(storageKey, value); } catch (error) { console.error(error); }
            state.page = 1;
            api.render({ deferMs: filterRenderDeferMs });
        }, listen);
    }

    function bindLength() {
        if (!lengthEl) return;
        lengthEl.value = String(state.pageLength);
        lengthEl.addEventListener('change', () => {
            state.pageLength = parseInt(lengthEl.value, 10) || defaultPageLength;
            state.page = 1;
            api.render();
        });
    }

    function bindSelectAll() {
        const all = grid.querySelector('#' + selectAllId) || grid.querySelector('.data-grid-header .data-grid-cell-check input');
        if (!all) return;
        all.checked = false;
        all.indeterminate = false;
        all.addEventListener('change', () => {
            body.querySelectorAll('.data-grid-item-visible').forEach((item) => {
                api.getItemCheckboxes(item).forEach((cb) => {
                    cb.checked = all.checked;
                });
            });
            api.emitSelectionChange();
        });
    }

    function bindCheckboxPressState() {
        const clearCheckboxPressState = () => {
            grid.querySelectorAll('.data-grid-row-checkbox-pressing').forEach(row => {
                row.classList.remove('data-grid-row-checkbox-pressing');
            });
        };

        grid.addEventListener('pointerdown', (e) => {
            const checkboxWrap = e.target.closest('.data-grid-cell-check, .data-grid-mobile-pill-checkbox-wrap');
            if (!checkboxWrap || !grid.contains(checkboxWrap)) return;
            const row = checkboxWrap.closest('.data-grid-row');
            if (!row) return;
            row.classList.add('data-grid-row-checkbox-pressing');
        });

        grid.addEventListener('pointerup', clearCheckboxPressState);
        grid.addEventListener('pointercancel', clearCheckboxPressState);
        grid.addEventListener('pointerleave', clearCheckboxPressState);
        document.addEventListener('pointerup', clearCheckboxPressState, listen);
    }

    grid.addEventListener('click', (e) => {
        const checkbox = e.target.closest('.' + checkboxClass + ', .data-grid-cell-check input');
        if (!checkbox || !grid.contains(checkbox)) return;
        e.stopPropagation();
    });

    grid.addEventListener('change', (e) => {
        const checkboxName = options.checkboxName;
        if (!checkboxName || e.target.getAttribute?.('name') !== checkboxName) return;
        const item = e.target.closest('.data-grid-item');
        if (item) {
            api.getItemCheckboxes(item).forEach((cb) => {
                cb.checked = e.target.checked;
            });
        }
        const visibleItems = body.querySelectorAll('.data-grid-item-visible');
        let checkedItemCount = 0;
        visibleItems.forEach((rowItem) => {
            if (api.getItemCheckboxes(rowItem).some((cb) => cb.checked)) {
                checkedItemCount++;
            }
        });
        const all = grid.querySelector('#' + selectAllId) || grid.querySelector('.data-grid-header .data-grid-cell-check input');
        if (all) {
            all.checked = checkedItemCount === visibleItems.length && visibleItems.length > 0;
            all.indeterminate = checkedItemCount > 0 && checkedItemCount < visibleItems.length;
        }
        api.emitSelectionChange();
    });

    bindSearch();
    bindDateRange();
    bindLength();
    bindSort(grid, () => ({ sortKey: state.sortKey, sortDir: state.sortDir }), (key, dir) => {
        state.sortDir = dir;
        state.sortKey = key;
        state.page = 1;
        api.render();
    });
    bindSelectAll();
    bindCheckboxPressState();
    bindFilter(statusFilterIds, statusStorageKey, (v) => { state.statusFilter = v; });
    bindFilter(recurringStatusFilterIds, recurringStatusStorageKey, (v) => { state.recurringStatusFilter = v; });
    bindFilter(typeFilterIds, typeStorageKey, (v) => { state.typeFilter = v; });

    bindMobileExpandDelegates(grid, createToggleRowHelpers(collapseOpenItems), listen);

    if (typeof options.onRowDetailsExpand === 'function') {
        grid.addEventListener('data-grid-row-details-expand', (e) => {
            const rowId = e.detail && e.detail.rowId;
            if (rowId) {
                options.onRowDetailsExpand(rowId);
            }
        }, listen);
    }

    document.addEventListener('data-grid-refresh', function onRefresh(e) {
        const d = e.detail || {};
        if (d.gridId && d.gridId !== grid.id) return;
        api.syncSearchQueryFromStorage();
        api.render();
    }, listen);

    document.addEventListener('data-grid-clear-date-range', function onClearDateRange(e) {
        const d = e.detail || {};
        if (!d.gridId || d.gridId !== grid.id) return;
        if (!dateRangeEnabled) return;
        state.dateFrom = '';
        state.dateTo = '';
        api.syncDateRangeInputs();
        state.page = 1;
        api.render();
        api.notifyDateRangeChange();
    }, listen);
}
